import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import { Trans, useTranslation } from '@lyearn/i18n';
import {
  AlertTriangleOutline,
  BulbOutline,
  CloseOutline,
  PlusOutline,
  SyncOutline,
} from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import {
  Button,
  DeprecatedIconButton,
  Divider,
  IconButton,
  Loader,
  Stack,
  Typography,
} from '@lyearn/ui';
import useAnalytics from '@/components/Analytics/hooks/useAnalytics';
import GradientText from '@/components/LyearnAI/components/GradientText';
import { suggestionsContainerStyles } from '@/components/LyearnAI/components/SuggestionsContainer/styles';
import { AI_COLORS, AI_LIST_ANIMATION } from '@/components/LyearnAI/consts';
import useQuestionOptions, { Suggestion } from '@/components/LyearnAI/hooks/useQuestionOptions';
import { LyearnAIProps } from '@/components/LyearnAI/types';
import { AnimatePresence, domAnimation, LazyMotion, MotionDiv } from '@/components/Motion';
import RichTextViewer from '@/components/RichTextViewer';
import AIFeedback from '@/features/ai-feedback';
import { GenerateOptionsFromQuizQuestionInputPropsType } from '@/types/schema';

type AIOptionsSuggestionsProps = Pick<LyearnAIProps, 'classes'> & {
  input: Pick<
    GenerateOptionsFromQuizQuestionInputPropsType,
    'question' | 'correctOptions' | 'incorrectOptions'
  >;
  showProtip?: boolean;
  onSubmit: (data: Suggestion[]) => void;
  hideAISuggestions: () => void;
  validateQuestionText: () => boolean;
};

const bulbStyles = css`
  fill: ${AI_COLORS.royalBlue};
`;

const SuggestionItem = ({
  onClickSuggestion,
  suggestion,
  isLoading,
}: {
  onClickSuggestion: (suggestion: Suggestion) => void;
  suggestion: Suggestion;
  isLoading: boolean;
}) => {
  return (
    <Stack
      alignItems="start"
      className="duration-50 linear w-full rounded-8 bg-bg-secondary py-10 ps-12 pe-16"
      gapX={16}>
      <IconButton
        Icon={
          isLoading
            ? () => <Loader arcColorToken="icon-secondary" circleColorToken="border" />
            : PlusOutline
        }
        appearance="secondary"
        className="mt-2 rounded-4 hover:text-icon-primary"
        shape="square"
        size="xs"
        variant="outlined"
        onClick={isLoading ? undefined : () => onClickSuggestion(suggestion)}
      />
      <RichTextViewer html={suggestion.questionOption} variant="body-short-01" />
    </Stack>
  );
};

const SuggestionList = ({
  suggestions,
  usedSuggestions,
  onClickSuggestion,
}: {
  suggestions: Suggestion[];
  usedSuggestions: Suggestion[];
  onClickSuggestion: (suggestion: Suggestion) => void;
}) => {
  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence initial={false} mode="sync">
        <MotionDiv
          animate={AI_LIST_ANIMATION.animate}
          className="flex w-full flex-col gap-y-6 px-16"
          exit={AI_LIST_ANIMATION.exit}
          initial={AI_LIST_ANIMATION.initial}
          transition={AI_LIST_ANIMATION.transition}>
          <AnimatePresence initial={false} mode="sync">
            {suggestions.length ? (
              suggestions.map((suggestion) => {
                return (
                  <MotionDiv
                    key={suggestion.questionOption}
                    animate={AI_LIST_ANIMATION.animate}
                    exit={AI_LIST_ANIMATION.exit}
                    initial={AI_LIST_ANIMATION.initial}
                    role="label"
                    transition={AI_LIST_ANIMATION.transition}>
                    <SuggestionItem
                      isLoading={
                        // is current suggestion in used suggestions?
                        usedSuggestions.findIndex(
                          (s) => s.questionOption === suggestion.questionOption,
                        ) !== -1
                      }
                      suggestion={suggestion}
                      onClickSuggestion={onClickSuggestion}
                    />
                  </MotionDiv>
                );
              })
            ) : (
              <Stack
                alignItems="start"
                className="h-46 w-full rounded-8 bg-bg-secondary py-10 ps-12 pe-16"
              />
            )}
          </AnimatePresence>
        </MotionDiv>
      </AnimatePresence>
    </LazyMotion>
  );
};

function AIOptionsSuggestions(props: AIOptionsSuggestionsProps) {
  const { classes, onSubmit, input, showProtip, hideAISuggestions, validateQuestionText } = props;

  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { suggestions, onGenerate, isGenerating, generativeTaskId, onAccceptSuggestion } =
    useQuestionOptions({
      input,
    });
  const [usedSuggestions, setUsedSuggestions] = useState<Suggestion[]>([]);
  const isShowingAIFeedback = !isGenerating && suggestions.length > 0;

  // add used suggestions to question.
  // debounce is necessary to avoid suggested option
  // jumping directly to question option, which makes
  // clicking other options harder.
  useDebounce(
    () => {
      if (!isGenerating && usedSuggestions.length > 0) {
        onSubmit(usedSuggestions);
        usedSuggestions.forEach((suggestion) => {
          onAccceptSuggestion(suggestion);
        });
        setUsedSuggestions([]);
      }
    },
    200,
    [usedSuggestions, isGenerating, onSubmit, onAccceptSuggestion],
  );

  const onSave = useCallback(
    (suggestion: Suggestion) => {
      setUsedSuggestions((prevUsedSuggestions) => [...prevUsedSuggestions, suggestion]);

      // hide when last suggestion is accepted
      if (suggestions.length <= 1) {
        hideAISuggestions();
      }
      track('Add incorrect option');
    },
    [hideAISuggestions, suggestions.length, track],
  );

  const onRegenerate = useCallback(() => {
    validateQuestionText() && onGenerate();
  }, [onGenerate, validateQuestionText]);

  // generate options on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onRegenerate, []);

  return (
    <Stack
      className={cn(
        classes?.open,
        suggestionsContainerStyles,
        'w-full rounded-12 border border-dashed border-bg-primary shadow-02',
      )}
      direction="col">
      <Stack
        alignItems="center"
        className={cn('w-full pt-12 pb-16 pe-12 ps-16')}
        justifyContent="between">
        <GradientText
          classes={{ icon: 'ms-8 h-20 w-20', text: 'ms-8' }}
          text={t('AI:Quiz.HeaderTitle')}
          variant="heading-xs"
        />
        <DeprecatedIconButton
          appearance="secondary"
          shape="square"
          size="medium"
          variant="ghost"
          onClick={hideAISuggestions}>
          <CloseOutline />
        </DeprecatedIconButton>
      </Stack>
      {showProtip ? (
        <Stack
          alignItems="center"
          className="mx-16 mb-20 rounded-8 bg-[#f7e7fd99] py-12 ps-12 pe-16"
          gapX={8}>
          <BulbOutline className={cn('h-16 w-16', bulbStyles)} />
          <Typography variant="body-short-02">
            <Trans i18nKey="AI:Quiz.ProTip" />
          </Typography>
        </Stack>
      ) : null}
      <SuggestionList
        suggestions={suggestions}
        usedSuggestions={usedSuggestions}
        onClickSuggestion={onSave}
      />
      {isShowingAIFeedback ? (
        <AIFeedback className={cn('mx-16 mt-24 -mb-8')} entityId={generativeTaskId} />
      ) : null}
      <Divider className="mt-24" />
      <Stack className="p-16 ps-24" justifyContent="between">
        <Stack alignItems="center" className={cn('gap-x-8')}>
          <AlertTriangleOutline className="h-20 w-20" fillColorToken={'icon-placeholder'} />
          <Typography color={'text-text-placeholder'} variant="body-short-02">
            {t('AI:AIResponsesCanBeInaccurateMisleading')}
          </Typography>
        </Stack>
        <Button
          StartIcon={SyncOutline}
          appearance="secondary"
          shape="square"
          size="small"
          variant="filled"
          onClick={() => {
            track('Regenerate incorrect option');
            onRegenerate();
          }}>
          {t('AI:Regenerate')}
        </Button>
      </Stack>
    </Stack>
  );
}

export default AIOptionsSuggestions;
